/**
 * src/services/teleportPlaceSearch.ts
 *
 * Forward-geocoding helper for the "Add a place to the teleport queue"
 * affordance in the cinematic AI-agent teleport HUD.
 *
 * Mirrors the cache-first pattern the GeoTrust MCP tools use server-
 * side (see `mcp-server/src/geocodingCache.js`):
 *
 *   1. Ask GeoLink first via the GeoTrust proxy at
 *      `GET /api/geocoding/cache?country=XX&query=...`. If the
 *      cache has a hit, we return immediately — zero Mapbox calls.
 *
 *   2. On miss (or `unavailable: true` soft-degrade), fall back to
 *      the Mapbox forward-geocoding REST API directly from the
 *      browser — same endpoint the existing SearchPanel uses, so we
 *      don't need a new backend route.
 *
 *   3. Persist the fresh Mapbox result back to GeoLink via
 *      `POST /api/geocoding/cache` so the next agent (or user) in
 *      the same country reuses it. This step is fire-and-forget
 *      (we don't await; failures are logged and swallowed).
 *
 * The function returns a normalised `TeleportPlaceResult` regardless
 * of which path it took, plus a `source` field the HUD can show in a
 * "Queued from cache" / "Queued from Mapbox" tooltip if it wants.
 */

import mapboxgl from 'mapbox-gl';

export type TeleportPlaceSource = 'geolink_cache' | 'mapbox' | 'mapbox_no_cache';

export interface TeleportPlaceResult {
  ok:        true;
  /** Normalised display name suitable for HUD ("Times Square, New York, …"). */
  placeName: string;
  /** WGS-84 latitude. */
  lat:       number;
  /** WGS-84 longitude. */
  lng:       number;
  /** ISO 3166-1 alpha-2 country (uppercase). May be `null` if the geocoder
   *  couldn't infer one (rare for forward-geocoding with `country=` filter). */
  country:   string | null;
  /** Where the result came from — useful for HUD telemetry & toasts. */
  source:    TeleportPlaceSource;
  /** Mapbox `place_id` style provider id, when available. */
  providerPlaceId: string | null;
}

export interface TeleportPlaceError {
  ok:    false;
  /** Human-readable message safe to surface in the HUD toast. */
  error: string;
  /** Machine-readable code: 'no_query' | 'no_token' | 'no_results' | 'fetch_failed'. */
  code:  'no_query' | 'no_token' | 'no_results' | 'fetch_failed';
}

/**
 * One typeahead suggestion. Mirrors the subset of a Mapbox feature
 * that the HUD's autocomplete dropdown actually needs — `id` is the
 * stable provider id (used as React key), `placeName` is what we
 * display, `lat`/`lng` is what we'd queue if the user picks this row.
 */
export interface TeleportPlaceSuggestion {
  id:        string;
  placeName: string;
  lat:       number;
  lng:       number;
  /** ISO 3166-1 alpha-2 inferred from the feature's context, if any. */
  country:   string | null;
}

export type TeleportPlaceOutcome = TeleportPlaceResult | TeleportPlaceError;

export interface SearchOpts {
  /**
   * Free-text query. Whitespace is trimmed; empty rejected. Stored
   * lower-cased in the cache key so "Coffee Shop" and "coffee shop"
   * collide correctly across users.
   */
  query: string;
  /**
   * ISO 3166-1 alpha-2 hint. Used as the GeoLink cache scope and
   * as a Mapbox `country=` filter so "Springfield" doesn't return
   * an Australian result when the user is teleporting around the
   * United States. Optional — global search if omitted, but cache
   * scoping degrades.
   */
  country?: string | null;
  /**
   * Wallet public key of the local user, forwarded to GeoLink as
   * `agent_public_key` so the cache row gets accurate
   * `last_used_by_agent` attribution. Anonymous joiners are fine.
   */
  walletPk?: string | null;
  /**
   * Geofence id to scope the cache lookup. Almost never set from
   * the GeoTrust HUD path — left in for future "teleport to a place
   * inside this geofence" flows.
   */
  geofenceId?: string | null;
}

const PROXY_BASE = '/api/geocoding/cache';

function _normalisedQuery(q: string): string {
  return q.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Cache-first forward geocode. Returns the first usable result.
 *
 * IMPORTANT: this is a single-shot lookup, NOT a typeahead. The HUD
 * uses an explicit submit (Enter or button click) so the user pays
 * one geocode per intent, not one per keystroke — matching the
 * GeoLink MCP team's per-agent rate-limit budget.
 */
export async function searchAndGeocodePlace(opts: SearchOpts): Promise<TeleportPlaceOutcome> {
  const raw = String(opts.query || '');
  const query = _normalisedQuery(raw);
  if (!query) {
    return { ok: false, error: 'Type a place to teleport to', code: 'no_query' };
  }
  const country = opts.country ? String(opts.country).toUpperCase() : null;

  // ── 1. GeoLink cache ──────────────────────────────────────────────
  // We always ask the cache first, even when there's no country hint —
  // the cache row may have been pinned to a geofence_id by an MCP
  // tool earlier. Soft-degrade silently: a 4xx/5xx from GeoLink is
  // not a teleport-blocking error; we just fall through to Mapbox.
  if (country) {
    try {
      const params = new URLSearchParams();
      params.set('country', country);
      params.set('query', query);
      if (opts.walletPk)   params.set('agent_public_key', String(opts.walletPk));
      if (opts.geofenceId) params.set('geofence_id', String(opts.geofenceId));
      const r = await fetch(`${PROXY_BASE}?${params.toString()}`, {
        method:  'GET',
        headers: { Accept: 'application/json' },
      });
      if (r.ok) {
        const j = await r.json().catch(() => null);
        if (j && j.cache_hit && j.data && Number.isFinite(j.data.lat) && Number.isFinite(j.data.lng)) {
          return {
            ok:              true,
            placeName:       j.data.place_name || j.data.city || raw,
            lat:             Number(j.data.lat),
            lng:             Number(j.data.lng),
            country:         (j.data.country || country || null) as string | null,
            providerPlaceId: j.data.provider_place_id || null,
            source:          'geolink_cache',
          };
        }
      }
    } catch {
      // Network blip on the cache check — fall through to Mapbox.
    }
  }

  // ── 2. Mapbox forward geocoding ───────────────────────────────────
  const token = mapboxgl.accessToken || (process.env.REACT_APP_MAPBOX_TOKEN || '');
  if (!token) {
    return {
      ok:    false,
      error: 'Map token not configured — cannot search places',
      code:  'no_token',
    };
  }

  try {
    const url = new URL(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(raw)}.json`);
    url.searchParams.set('access_token', token);
    url.searchParams.set('limit', '1');
    if (country) url.searchParams.set('country', country.toLowerCase());
    const r = await fetch(url.toString());
    if (!r.ok) {
      return { ok: false, error: `Mapbox returned HTTP ${r.status}`, code: 'fetch_failed' };
    }
    const j = await r.json();
    const feat = Array.isArray(j?.features) && j.features.length > 0 ? j.features[0] : null;
    if (!feat || !Array.isArray(feat.center) || feat.center.length < 2) {
      return { ok: false, error: `No results for "${raw}"`, code: 'no_results' };
    }
    const [lng, lat] = feat.center;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return { ok: false, error: `No results for "${raw}"`, code: 'no_results' };
    }

    // Try to surface the actual ISO country code from the feature
    // context — Mapbox returns `country.XX` as the short_code on
    // the matching context entry. Fallback to the requested filter.
    const ctxCountry = Array.isArray(feat.context)
      ? (feat.context.find((c: any) => typeof c?.id === 'string' && c.id.startsWith('country.'))?.short_code || '').toUpperCase()
      : '';
    const inferredCountry = ctxCountry || country || null;

    // ── 3. Persist back to GeoLink (fire-and-forget) ────────────────
    // We don't await this — the queue insertion shouldn't wait on
    // the cache write. A failed POST just means the next user pays
    // another Mapbox call; we log and move on. There's nothing
    // useful we can return to the caller about success/failure of
    // this background write either, so we drop the resolved value
    // entirely (callers learn from the upstream response on their
    // NEXT search, when the row will already be in the cache).
    if (inferredCountry) {
      try {
        const postBody = {
          country:           inferredCountry,
          query:             raw,
          normalized_query:  query,
          city:              feat.text || null,
          place_name:        feat.place_name || raw,
          lat,
          lng,
          provider:          'mapbox',
          provider_place_id: feat.id || null,
          geofence_id:       opts.geofenceId || null,
          agent_public_key:  opts.walletPk || null,
          mcp_tool:          'geotrust_teleport_add_place',
          raw:               { features: [feat] },
        };
        void fetch(PROXY_BASE, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(postBody),
        }).catch(() => { /* swallow */ });
      } catch {
        // Body construction failure (extremely unlikely) — drop on the floor.
      }
    }

    return {
      ok:              true,
      placeName:       feat.place_name || feat.text || raw,
      lat:             Number(lat),
      lng:             Number(lng),
      country:         inferredCountry,
      providerPlaceId: feat.id || null,
      // We don't actually wait for the savedToCache promise above
      // (intentionally — the queue insertion shouldn't block on a
      // cache write), so we report `mapbox` if we kicked the POST
      // off and `mapbox_no_cache` if there was no country to scope
      // the write under. Either way the user already has their
      // teleport target.
      source:          inferredCountry ? 'mapbox' : 'mapbox_no_cache',
    };
  } catch (err) {
    return {
      ok:    false,
      error: 'Could not reach Mapbox to look up that place',
      code:  'fetch_failed',
    };
  }
}

/**
 * Forward-geocoding TYPEAHEAD — returns up to 5 suggestions for the
 * autocomplete dropdown in the teleport HUD. Distinct from
 * `searchAndGeocodePlace` (which is a single-shot resolve + cache
 * write) because typeahead fires on every keystroke and we don't want
 * to pay the GeoLink cache write cost or hit our cache on partial
 * queries that the user is still typing.
 *
 * Implementation notes:
 *   - We use Mapbox's `autocomplete=true` flag which is optimised for
 *     prefix matches and returns ranked results.
 *   - We don't consult the GeoLink cache here — the cache is keyed by
 *     normalised exact-query, so it would never match prefixes; it
 *     pays off on the SUBMISSION path (searchAndGeocodePlace), not
 *     here.
 *   - Aborts via AbortController are supported so a faster keystroke
 *     can cancel an in-flight typeahead and replace it.
 */
export async function suggestPlaces(
  query:   string,
  opts: { country?: string | null; signal?: AbortSignal } = {},
): Promise<TeleportPlaceSuggestion[]> {
  const raw = String(query || '').trim();
  if (raw.length < 2) return [];                  // typing too short — wait
  const token = mapboxgl.accessToken || (process.env.REACT_APP_MAPBOX_TOKEN || '');
  if (!token) return [];

  try {
    const url = new URL(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(raw)}.json`);
    url.searchParams.set('access_token', token);
    url.searchParams.set('autocomplete', 'true');
    url.searchParams.set('limit', '5');
    if (opts.country) url.searchParams.set('country', opts.country.toLowerCase());
    const r = await fetch(url.toString(), { signal: opts.signal });
    if (!r.ok) return [];
    const j = await r.json();
    const feats = Array.isArray(j?.features) ? j.features : [];
    const out: TeleportPlaceSuggestion[] = [];
    for (const feat of feats) {
      if (!Array.isArray(feat?.center) || feat.center.length < 2) continue;
      const [lng, lat] = feat.center;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const ctxCountry = Array.isArray(feat.context)
        ? (feat.context.find((c: any) => typeof c?.id === 'string' && c.id.startsWith('country.'))?.short_code || '').toUpperCase()
        : '';
      out.push({
        id:        String(feat.id || `${feat.place_name}|${lat},${lng}`),
        placeName: String(feat.place_name || feat.text || raw),
        lat:       Number(lat),
        lng:       Number(lng),
        country:   ctxCountry || (opts.country ? opts.country.toUpperCase() : null),
      });
    }
    return out;
  } catch (err: any) {
    // Aborted typeahead is the common case — silently ignore.
    if (err && (err.name === 'AbortError' || err.code === 20)) return [];
    return [];
  }
}
